/* eslint-disable @typescript-eslint/indent */

/* Note: please be careful with indentation, we have the rule disabled because of
*  eslint not being able to properly indent js inside styled components
*/

// Core
import styled from 'styled-components';

// Libraries
import { Modal as AntDModal } from 'antd';

// Config
import { BREAKPOINTS } from '../../../config/screen';

// Plugins
import { hexToRgba } from '../../../plugins/general';

// Component
import { ModalPanelProps } from './ModalPanel.types';

export const TitleContainer = styled.div`
  padding-left: 0.75rem;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding-left: 1rem;
  };

  @media (min-width: ${BREAKPOINTS.lg}px) {
    padding-left: 1.25rem;
  };
`;

export const BackButtonContainer = styled.div`
  margin-bottom: 0.75rem;
  padding-top: 0.75rem;

  @media (min-width: ${BREAKPOINTS.lg}px) {
    margin-bottom: 1.125rem;
    padding-top: 1rem;
  };
`;

export const CloseButtonContainer = styled.div`
  padding-top: 0.75rem;
  flex-shrink: 0;

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding-top: 1rem;
  };

  @media (min-width: ${BREAKPOINTS.lg}px) {
    padding-top: 1.75rem;
  };
`;

export const DescriptionContainer = styled.div`
  padding-left: 0.75rem;
  margin-top: 0.75rem;

  @media (min-width: ${BREAKPOINTS.lg}px) {
    padding-left: 1.25rem;
    margin-top: 1rem;
  };
`;

export const HeaderContent = styled.div<{ hasBackButton?: boolean }>`
  margin-right: 0.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding-top: ${({ hasBackButton }) => (hasBackButton ? '1em' : '0')};

  @media (min-width: ${BREAKPOINTS.lg}px) {
    padding-top: ${({ hasBackButton }) => (hasBackButton ? '1.75em' : '0')};
  };
`;

export const Header = styled.div<Pick<ModalPanelProps, 'description'>>`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0 1rem 0 0.25rem;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: 0 1rem 0 0.5rem;
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: 0 1rem 0 0.5rem;
  };

  @media (min-width: ${BREAKPOINTS.lg}px) {
    padding: 0 1.75rem 0 0.5rem;
  };
`;

export const Container = styled(AntDModal)<{
  height?: string,
  width?: string,
}>`
  overflow: hidden;
  display: flex;

  /* Mobile and Tablet: near fullscreen */
  width: 95dvw !important;
  height: 95dvh !important;

  @media (min-width: ${BREAKPOINTS.lg}px) {
    width: ${({ width }) => width || '85%'} !important;
    height: ${({ height }) => height || 'auto'} !important;
  };

  .modal-header-description {
    color: ${({ theme }) => theme.color.modalPanel.headerDescriptionTextColor};
  };

  .ant-modal, .ant-modal-content {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  };
  
  .ant-modal-body {
    display: flex;
    flex-direction: column;
    row-gap: 0.5rem;
    height: 100%;

    @media (min-width: ${BREAKPOINTS.md}px) {
      row-gap: 1rem;
    };

    @media (min-width: ${BREAKPOINTS.lg}px) {
      row-gap: 1.75rem;
    };
  };
`;

export const Content = styled.div<{ hasScroll: boolean }>`
  padding: ${({ hasScroll }) => (hasScroll ? '0 0.625rem 1rem 1rem' : '0 1rem 1rem 1rem')};
  position: relative;
  overflow-y: auto;
  flex: 1;

  @media (min-width: ${BREAKPOINTS.sm}px) {
    padding: ${({ hasScroll }) => (hasScroll ? '0 1.125rem 1rem 1.5rem' : '0 1.5rem 1rem 1.5rem')};
  };

  @media (min-width: ${BREAKPOINTS.md}px) {
    padding: ${({ hasScroll }) => (hasScroll ? '0 0.75rem 1rem 1rem' : '0 1rem 1rem 1rem')};
  };

  @media (min-width: ${BREAKPOINTS.lg}px) {
    padding: ${({ hasScroll }) => (
      hasScroll
        ? '0 1.375rem 1.75rem 1.75rem'
        : '0 1.75rem 1.75rem 1.75rem'
    )};
  };

  ::-webkit-scrollbar {
    width: 0.438rem;
  };

  ::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 3.375rem;
  };

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.color.modalPanel.scrollBackgroundColor};
    border-radius: 3.375rem;
  };
`;

export const GradientEffect = styled.div<{ isHidden: boolean }>`
  position: absolute;
  bottom: 0;
  right: 0.78rem;
  height: 14.25rem;
  width: 100%;
  border-radius: 4px;
  opacity: ${({ isHidden }): number => (isHidden ? 0 : 1)};
  background: linear-gradient(
    180deg,
    ${({ theme }) => hexToRgba(theme.color.modalPanel.colorGradient, 0)},
    ${({ theme }) => theme.color.modalPanel.colorGradient} 100%
  );
  transition: opacity 0.3s ease;
  pointer-events: none;
`;
